// src/components/IntroEnemy.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styles from '../css/modules/IntroEnemy.module.css';
import EnergyBar from './EnergyBar';
import Typewriter from './Typewriter';
import AnalogCounter from './AnalogCounter';
import { useUser } from './UserContext';

/* Tło ekranu startowego jest stałe, a nie losowane.
   Powód jest wydajnościowy: prerender zapisuje w build/intro/index.html
   konkretny adres obrazka, więc przy losowaniu przeglądarka pobierała
   najpierw tło z prerenderu, a potem drugie (i trzecie, przy kolejnych
   renderach) wylosowane w React. Stały plik pobiera się raz i zostaje
   w cache na kolejne wizyty. */
const BACKGROUND_IMAGE_URL = '/backgrounds/1.webp';

/* Prerender (headless Chrome uderzający w lokalny serwer) nigdy nie dostanie
   danych przeciwnika - API odrzuca żądanie z origin http://localhost.
   Bez tego w build/intro/index.html zapisywał się komunikat o błędzie
   i każdy użytkownik widział go przez ułamek sekundy przed startem aplikacji. */
const isPrerender =
  typeof navigator !== 'undefined' && /HeadlessChrome/i.test(navigator.userAgent);

const IntroEnemy = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();

  // Domyślne dane użytkownika - używane, gdy nie mamy danych z kontekstu ani przekazanych
  const defaultUserData = {
    image: 'my-photo', // podajemy tylko nazwę, reszta jest dodawana przy wyświetlaniu
    name: 'Gracz',
    energy: 100,
    score: 550,
    ranking: 0,
    level: 0,
    artefacts: [],
  };

  const passedUserData = location.state?.userData;
  // Jeśli użytkownik jest zalogowany, używamy danych z kontekstu; w przeciwnym razie - danych przekazanych lub domyślnych.
  const [userData, setUserData] = useState(user || passedUserData || defaultUserData);

  // Stan dla danych przeciwnika
  const [enemy, setEnemy] = useState(null);
  const [loadingEnemy, setLoadingEnemy] = useState(true);
  const [errorEnemy, setErrorEnemy] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Pobieramy dane przeciwnika
  const fetchEnemyData = useCallback(async () => {
    setLoadingEnemy(true);
    setErrorEnemy(null);
    try {
      /* Żądanie startuje już w <head> (public/index.html), zanim przeglądarka
         pobierze i wykona bundle - oszczędza ok. 0,5 s oczekiwania na przycisk
         WALCZ. Tu tylko odbieramy gotową obietnicę; kolejne próby idą normalnie. */
      const pending = window.__enemyRequest;
      window.__enemyRequest = null;

      const response = await (pending ||
        fetch(`${API_BASE_URL}/cyber/get_random_enemy`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        }));
      if (!response.ok) {
        throw new Error('Błąd sieciowy: nie udało się pobrać danych przeciwnika');
      }
      const data = await response.json();
      const imageUrl = `/enemies/${data.slug}.webp`;
      setEnemy({
        image: imageUrl,
        name: data.name,
        health: data.health,
        slug: data.slug,
        strength: data.strength,
        time: data.time,
      });
    } catch (err) {
      console.error('Błąd podczas pobierania danych przeciwnika:', err);
      setErrorEnemy(err.message);
    } finally {
      setLoadingEnemy(false);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    fetchEnemyData();
  }, [fetchEnemyData]);

  /* Rozgrzewamy paczkę quizu, gdy użytkownik jest już na ekranie startowym -
     kliknie WALCZ/START za chwilę, a paczka jest wtedy w cache. Wcześniej
     robił to webpackPrefetch przy imporcie trasy, ale ten dokładał ~50 kB
     pobierania do KAŻDEJ podstrony (także cennika i poradników).
     W headless Chrome pomijamy: inaczej prerender uznałby tę paczkę za
     część strony i dokleiłby jej <link rel="preload">. */
  useEffect(() => {
    if (typeof navigator !== 'undefined' && /HeadlessChrome/i.test(navigator.userAgent)) return undefined;
    const timer = setTimeout(() => {
      import('./QuizCyberpunkSingle').catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // Jeśli użytkownik jest zalogowany, używamy jego danych.
  useEffect(() => {
    if (user) {
      setUserData(user);
    }
  }, [user]);

  // Aktualizacja energii użytkownika już przy renderowaniu komponentu,
  // jeżeli energia jest mniejsza niż 0.
  useEffect(() => {
    const updateEnergy = async () => {
      /* Dolewka rowna pelnej energii startowej (100), nie 10.
         Sila przeciwnikow to 5-20, a kazda bledna odpowiedz zabiera cala sile,
         wiec przy 10 pierwsza pomylka konczyla pojedynek - gracz ZALOGOWANY mial
         gorzej niz anonimowy, ktory startuje ze 100 (patrz defaultUserData wyzej). */
      if (userData && userData.energy < 1) {
        const updatedUserData = { ...userData, energy: 100 };
        setUserData(updatedUserData);
        const updatedData = { energy: 100 };
        console.log('Aktualizacja energii przy starcie, payload:', updatedData);
        try {
          const token = localStorage.getItem('access_token');
          const response = await fetch(`${API_BASE_URL}/cyber/user_info`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(updatedData),
          });
          if (!response.ok) {
            throw new Error('Błąd aktualizacji danych użytkownika');
          }
          const data = await response.json();
          console.log('User info updated in DB:', data);
        } catch (error) {
          console.error('Error updating user info in DB:', error);
        }
      }
    };
    updateEnergy();
  }, [userData, API_BASE_URL]);

  // Obsługa przycisku "Walcz" - przekierowanie do quizu
  const handleContinue = () => {
    if (enemy) {
      navigate(`/quiz/${enemy.slug}`, { state: { enemyData: enemy, userData } });
    }
  };

  // Obsługa przycisku "Sklep" - przekierowanie do sklepu z przekazaniem aktualnego wyniku (score)
  const handleShop = () => {
    navigate('/shop', { state: { score: userData.score } });
  };

  const containerStyle = {
    backgroundImage: `linear-gradient(135deg, rgba(13, 13, 13, 0.7), rgba(26, 26, 26, 0.7)), url(${BACKGROUND_IMAGE_URL})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  };

  // Nagłówek widoczny w każdym stanie ekranu: tłumaczy nowemu użytkownikowi,
  // co robi, i daje stronie realną treść (H1) dla wyszukiwarek.
  const introHeader = (
    <header className={styles.introHeader}>
      <h1>Darmowy test na patent strzelecki</h1>
      <p>
        Stajesz do pojedynku na pytania z egzaminu PZSS: ustawa o broni i amunicji, zasady
        bezpieczeństwa i regulamin strzelnicy. Kliknij <strong>WALCZ</strong>, żeby rozpocząć -
        bez rejestracji i bez opłat.
      </p>
    </header>
  );

  /* Ekran ma zawsze ten sam układ - także zanim dojdą dane przeciwnika.
     Wcześniej pokazywaliśmy w tym miejscu goły komunikat („Ładowanie danych…”
     albo błąd), więc strona dwa razy przeskakiwała, a prerender zapisywał
     w HTML-u komunikat o błędzie zamiast właściwego ekranu. */
  const enemyReady = Boolean(enemy);
  const showError = Boolean(errorEnemy) && !isPrerender;

  return (
    <div className={styles.cyberpunkContainer} style={containerStyle}>
      {introHeader}
      {showError && (
        <p className={styles.loadError}>
          Nie udało się pobrać przeciwnika.{' '}
          <button type="button" className={styles.retryButton} onClick={fetchEnemyData}>
            Spróbuj ponownie
          </button>
        </p>
      )}
      <div className={styles.fightContainer}>
        {/* Box przeciwnika */}
        <div className={styles.enemyBox}>
          {enemyReady ? (
            <img src={enemy.image} alt={enemy.name} className={styles.cyberImage} width="400" height="400" />
          ) : (
            <div className={`${styles.cyberImage} ${styles.imagePlaceholder}`} aria-hidden="true" />
          )}
          <h2 className={styles.enemyName}>
            {enemyReady ? <Typewriter text={enemy.name} typingSpeed={100} /> : 'Przeciwnik'}
          </h2>
          <div className={styles.stats}>
            <div className={styles.statRow}>
              <span className={styles.statLabel}><strong>Siła:</strong></span>
              <span className={styles.statValue}>
                <AnalogCounter value={(enemy && enemy.strength) || 0} />
              </span>
            </div>
            <div className={styles.statRow}>
              <span className={styles.statLabel}><strong>Energia:</strong></span>
              <span className={styles.statValue}>
                <EnergyBar current={enemy ? enemy.health : 0} />
              </span>
            </div>
            <div className={styles.statRow}>
              <span className={styles.statLabel}><strong>Szybkość:</strong></span>
              <span className={styles.statValue}>
                <AnalogCounter value={(enemy && enemy.time) || 0} />
              </span>
            </div>
          </div>
        </div>

        <div className={styles.buttonContainer}>
        <button
          className={styles.startBattleButton}
          onClick={handleContinue}
          disabled={!enemyReady}
          aria-busy={loadingEnemy}
        >
          WALCZ
        </button>
      </div>

{/* Box użytkownika */}
<div className={styles.userBox}>
  <img src={`/${userData.image}.webp`} alt={userData.name} className={styles.cyberImage} width="400" height="400" />
  <h2 className={styles.userName}>{userData.name}</h2>
  <div className={styles.stats}>
    <div className={styles.statRow}>
      <span className={styles.statLabel}><strong>Kasa:</strong></span>
      <span className={styles.statValue}>
        <AnalogCounter value={userData.score || 0} />
      </span>
    </div>
    <div className={styles.statRow}>
      <span className={styles.statLabel}><strong>Energia:</strong></span>
      <span className={styles.statValue}>
        <EnergyBar current={userData.energy} />
      </span>
    </div>
    <div className={styles.statRow}>
      <span className={styles.statLabel}><strong>Level:</strong></span>
      <span className={styles.statValue}>
        <AnalogCounter value={userData.level || 0} />
      </span>
    </div>
    <div className={styles.statRow}>
      <span className={styles.statLabel}><strong>Dobre odp:</strong></span>
      <span className={styles.statValue}>
        <AnalogCounter value={userData.answered_correct || 0} />
      </span>
    </div>
  </div>
  <div className={styles.shopButtonContainer}>
    <button className={styles.shopButton} onClick={handleShop}>
      Sklep
    </button>
  </div>
  {/* Sekcja artefaktów użytkownika z małymi zdjęciami */}
  {userData.artefacts && (
  <div className={styles.artefactsContainer}>
    <div className={styles.artefactsBox}>
      {Array.from({ length: 4 }, (_, index) => {
        const artifact = userData.artefacts[index];
        return (
          <div key={index} className={styles.artifactSlot}>
            {artifact ? (
              <img
                src={`/artefacts/${artifact}.webp`}
                alt={`Artefakt ${index + 1}`}
                className={styles.artifactImage}
              />
            ) : (
              <div className={styles.emptyArtifact} />
            )}
          </div>
        );
      })}
    </div>
  </div>
)}
</div>

      </div>
    </div>
  );
};

export default IntroEnemy;
